<template>
<div v-if="metadata || layerDescriptor">
  <div class="panel-subtitle">{{ $t('names.metadata') | capitalize }}</div>
  <q-list separator dense>
    <q-item v-if="layerDescriptor">
      <q-item-section>
        <q-item-label>{{ layerDescriptor.name }}</q-item-label>
        <q-item-label caption v-if="layerDescriptor.href">
          <a :href="layerDescriptor.href" target="_blank">{{ layerDescriptor.text }}</a>
        </q-item-label>
        <q-item-label caption v-else>{{ layerDescriptor.text }}</q-item-label>
      </q-item-section>
      <q-item-section side>
        <div>
          <q-btn flat dense no-caps
            v-if="layerDescriptor.type.toLowerCase() === 'geojson'"
            icon="search"
            @click="open(layerDescriptor.text)"
          >
            <q-tooltip>{{ $t('actions.explore') | capitalize }}</q-tooltip>
          </q-btn>
          <q-btn flat dense no-caps
            v-else-if="layerDescriptor.type.toLowerCase() === 'wms'"
            icon="search"
            @click="open(layerDescriptor.href)"
          >
            <q-tooltip>{{ $t('actions.explore') | capitalize }}</q-tooltip>
          </q-btn>
          <q-btn flat dense no-caps
            v-clipboard:copy="layerDescriptor.text"
            v-clipboard:success="doCopy"
            :icon="copied ? 'fas fa-check' : 'fas fa-copy'"
            :color="copied ? 'green' : void 0"
          >
            <q-tooltip>{{ $t('actions.copy') + ' URL' | capitalize }}</q-tooltip>
          </q-btn>
        </div>
      </q-item-section>
    </q-item>
    <q-item v-if="layerDescriptor && layerDescriptor.type">
      <q-item-section>{{ $t('names.type') | capitalize }}</q-item-section>
      <q-item-section side>{{ layerDescriptor.type }}</q-item-section>
    </q-item>
    <q-item v-if="layerDescriptor && layerDescriptor.projection">
      <q-item-section>{{ $t('names.projection') | capitalize }}</q-item-section>
      <q-item-section side>{{ layerDescriptor.projection }}</q-item-section>
    </q-item>
    <q-item v-for="item in metadataList" :key="item.name">
      <q-item-section v-if="item.data.length < 60">{{ item.name | capitalize }}</q-item-section>
      <q-item-section v-if="item.data.length < 60 && item.type === 'web'" side>
        <a :href="item.data" target="_blank">{{ item.data }}</a>
      </q-item-section>
      <q-item-section v-else-if="item.data.length < 60" side>
        {{ item.data }}
      </q-item-section>
      <q-item-section v-else>
        <q-item-label>{{ item.name | capitalize }}</q-item-label>
        <q-item-label caption>{{ item.data }}</q-item-label>
      </q-item-section>
    </q-item>
  </q-list>
  <div v-if="bbox">
    <p class="panel-subtitle">{{ $t('names.map') | capitalize }}</p>
    <l-map
      style="height: 350px; width: 100%"
      ref="map"
      :center="mapCenter"
      :bounds="bounds"
    >
      <l-tile-layer :url="tileLayerURL" />
      <l-polygon
        color="red"
        dashArray="5, 5"
        fillColor="red"
        :fillOpacity="0.05"
        :interactive="false"
        :lat-lngs="bboxPolygon"
      />
    </l-map>
  </div>
</div>
</template>

<script>
import { QBtn, QItem, QItemSection, QItemLabel, QList, QTooltip } from 'quasar'
import { codeToNativeName } from 'src/lib/language'
import { LMap, LTileLayer, LPolygon } from 'vue2-leaflet'

export default {
  props: [
    'layerDescriptor',
    'metadata'
  ],
  components: {
    LMap, LTileLayer, LPolygon, QBtn, QItem, QItemSection, QItemLabel, QList, QTooltip
  },
  data () {
    return {
      copied: false,
      tileLayerURL: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    }
  },
  computed: {
    bbox () {
      return this.metadata && this.metadata.bbox && this.metadata.bbox.split(',').map(coord => parseFloat(coord))
    },
    bboxPolygon () {
      return this.bbox && [
        [this.bbox[1], this.bbox[0]],
        [this.bbox[3], this.bbox[0]],
        [this.bbox[3], this.bbox[2]],
        [this.bbox[1], this.bbox[2]]
      ]
    },
    bounds () {
      return [[this.bbox[1], this.bbox[0]], [this.bbox[3], this.bbox[2]]]
    },
    layer () {
      return null
    },
    map () {
      return this.$refs.map
    },
    mapCenter () {
      return this.bbox && [(this.bbox[1] + this.bbox[3]) * 0.5, (this.bbox[0] + this.bbox[2]) * 0.5]
    },
    metadataList () {
      if (!this.metadata) {
        return
      }
      let metadataList_ = []
      if (this.date) {
        metadataList_.push(this.listItem('date', this.date))
      }
      if (this.metadata.summary) {
        metadataList_.push(this.listItem('summary', this.metadata.summary))
      }
      if (this.metadata.category) {
        metadataList_.push(this.listItem('category', this.metadata.category))
      }
      if (this.metadata.language) {
        metadataList_.push(this.listItem('language', this.metadata.language, 'lang'))
      }
      if (this.metadata.information) {
        metadataList_.push(this.listItem('information', this.metadata.information))
      }
      if (this.metadata.provider_web) {
        metadataList_.push(this.listItem('provider_web', this.metadata.provider_web, 'web'))
      }
      if (this.metadata.provider_name) {
        metadataList_.push(this.listItem('provider_name', this.metadata.provider_name))
      }
      if (this.metadata.provider_email) {
        metadataList_.push(this.listItem('provider_email', this.metadata.provider_email))
      }
      return metadataList_
    }
  },
  methods: {
    doCopy () {
      this.copied = true
    },
    listItem (name, data, type = null) {
      name = this.$t(`metadata.${name}`)
      data = data.trim()
      if (type === 'lang') {
        data = codeToNativeName(data)
      }
      return { name, data, type }
    },
    open (url) {
      window.open(url)
    }
  }
}
</script>

<style>

</style>
